import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, ChevronDown, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import {
  createEmergencyResponsePlan,
  getEmergencyResponsePlanById,
  updateEmergencyResponsePlan,
} from "../../../service/api";
import { useRoleManageStore } from "../../../store/store";

interface EmergencyResponseStep {
  id: string;
  stepDescription: string;
  order: number;
}

interface EmergencyResponsePlanFormData {
  erpName: string;
  scenarioType: string;
  scenarioTypeOther: string;
  steps: EmergencyResponseStep[];
  assignedRoleId: string;
  responseTimeSLA: number | "";
}

interface FormErrors {
  erpName?: string;
  scenarioType?: string;
  scenarioTypeOther?: string;
  steps?: string;
  assignedRoleId?: string;
  responseTimeSLA?: string;
}

const SCENARIO_TYPES = [
  { value: "Fire", label: "Fire" },
  { value: "Earthquake", label: "Earthquake" },
  { value: "Medical", label: "Medical" },
  { value: "Security", label: "Security" },
  { value: "Other", label: "Other" },
];

const AddResponsePlan: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("id");
  const mode = searchParams.get("mode") || "create";

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const [formData, setFormData] = useState<EmergencyResponsePlanFormData>({
    erpName: "",
    scenarioType: "",
    scenarioTypeOther: "",
    steps: [],
    assignedRoleId: "",
    responseTimeSLA: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const { rolesData, fetchAllRoles, loading: rolesLoading } =
    useRoleManageStore();

  useEffect(() => {
    fetchAllRoles();
  }, [fetchAllRoles]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && planId) {
      const fetchPlan = async () => {
        setLoading(true);
        try {
          const response = await getEmergencyResponsePlanById(planId);
          if (response.data?.resCode === 200 && response.data?.data) {
            const plan = response.data.data;
            setFormData({
              erpName: plan.erpName || "",
              scenarioType: plan.scenarioType || "",
              scenarioTypeOther: plan.scenarioTypeOther || "",
              steps:
                plan.steps?.map((step: any, index: number) => ({
                  id: `step-${index}-${Date.now()}`,
                  stepDescription: step.stepDescription || "",
                  order: step.order || index + 1,
                })) || [],
              assignedRoleId: plan.assignedRoleId || "",
              responseTimeSLA: plan.responseTimeSLA || "",
            });
          } else {
            toast.error("Emergency Response Plan not found.");
            navigate("/external-emergency-preparedness/emergency-response-plans");
          }
        } catch (error: any) {
          console.error("Error fetching plan:", error);
          toast.error(
            error.response?.data?.resMessage ||
              "Failed to fetch Emergency Response Plan"
          );
          navigate("/external-emergency-preparedness/emergency-response-plans");
        } finally {
          setLoading(false);
        }
      };
      fetchPlan();
    }
  }, [planId, mode, isEditMode, isViewMode, navigate]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.erpName.trim()) {
      newErrors.erpName = "ERP Name is required";
    }

    if (!formData.scenarioType) {
      newErrors.scenarioType = "Scenario Type is required";
    }

    if (formData.scenarioType === "Other" && !formData.scenarioTypeOther.trim()) {
      newErrors.scenarioTypeOther = "Please specify the scenario type";
    }

    if (formData.steps.length === 0) {
      newErrors.steps = "At least one step is required";
    } else {
      const emptySteps = formData.steps.filter(
        (step) => !step.stepDescription.trim()
      );
      if (emptySteps.length > 0) {
        newErrors.steps = "All steps must have a description";
      }
    }

    if (!formData.assignedRoleId) {
      newErrors.assignedRoleId = "Assigned Role/Team is required";
    } else {
      const roleExists = rolesData.some(
        (role) => role._id === formData.assignedRoleId
      );
      if (!roleExists) {
        newErrors.assignedRoleId =
          "Selected role does not exist in Role Management";
      }
    }

    if (formData.responseTimeSLA === "" || formData.responseTimeSLA === null) {
      newErrors.responseTimeSLA = "Response Time SLA is required";
    } else if (
      typeof formData.responseTimeSLA === "number" &&
      formData.responseTimeSLA <= 0
    ) {
      newErrors.responseTimeSLA = "Response Time SLA must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof EmergencyResponsePlanFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleAddStep = () => {
    const newStep: EmergencyResponseStep = {
      id: `step-${Date.now()}-${Math.random()}`,
      stepDescription: "",
      order: formData.steps.length + 1,
    };

    setFormData((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));

    if (errors.steps) {
      setErrors((prev) => ({
        ...prev,
        steps: undefined,
      }));
    }
  };

  const handleRemoveStep = (stepId: string) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps
        .filter((step) => step.id !== stepId)
        .map((step, index) => ({
          ...step,
          order: index + 1,
        })),
    }));
  };

  const handleStepChange = (stepId: string, description: string) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === stepId ? { ...step, stepDescription: description } : step
      ),
    }));

    if (errors.steps) {
      setErrors((prev) => ({
        ...prev,
        steps: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) return;

    if (!validateForm()) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: any = {
        erpName: formData.erpName.trim(),
        scenarioType: formData.scenarioType,
        steps: formData.steps.map((step) => ({
          stepDescription: step.stepDescription.trim(),
          order: step.order,
        })),
        assignedRoleId: formData.assignedRoleId,
        responseTimeSLA: Number(formData.responseTimeSLA),
      };

      if (formData.scenarioType === "Other" && formData.scenarioTypeOther.trim()) {
        payload.scenarioTypeOther = formData.scenarioTypeOther.trim();
      }

      let response;
      if (isEditMode && planId) {
        response = await updateEmergencyResponsePlan(planId, payload);
      } else {
        response = await createEmergencyResponsePlan(payload);
      }

      if (response.data?.resCode === 200 || response.data?.resCode === 201) {
        toast.success(
          response.data?.resMessage ||
            `Emergency Response Plan ${isEditMode ? "updated" : "created"} successfully`
        );
        navigate("/external-emergency-preparedness/emergency-response-plans");
      } else {
        throw new Error(
          response.data?.resMessage ||
            `Failed to ${isEditMode ? "update" : "create"} Emergency Response Plan`
        );
      }
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} Emergency Response Plan:`,
        error
      );
      const errorMessage =
        error.response?.data?.resMessage ||
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEditMode ? "update" : "create"} Emergency Response Plan`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isViewMode
    ? "View Emergency Response Plan"
    : isEditMode
    ? "Edit Emergency Response Plan"
    : "Create Emergency Response Plan";

  if (loading) {
    return (
      <div className="px-2 py-4 mt-14 ml-2 min-h-screen flex justify-center items-center">
        <p className="text-gray-500 text-lg">Loading plan data...</p>
      </div>
    );
  }

  return (
    <div className="px-2 py-4 mt-14 ml-2 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={() =>
              navigate("/external-emergency-preparedness/emergency-response-plans")
            }
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
            title="Back to list"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ERP Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="erpName"
                className="text-sm font-medium text-gray-700"
              >
                ERP Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="erpName"
                value={formData.erpName}
                onChange={(e) => handleInputChange("erpName", e.target.value)}
                disabled={isViewMode}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.erpName ? "border-red-500" : "border-gray-300"
                } ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                placeholder="Enter Emergency Response Plan name"
                required
              />
              {errors.erpName && (
                <p className="text-red-500 text-sm">{errors.erpName}</p>
              )}
            </div>

            {/* Scenario Type */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="scenarioType"
                className="text-sm font-medium text-gray-700"
              >
                Scenario Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="scenarioType"
                  value={formData.scenarioType}
                  onChange={(e) =>
                    handleInputChange("scenarioType", e.target.value)
                  }
                  disabled={isViewMode}
                  className={`w-full p-2 border rounded-md appearance-none focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                    errors.scenarioType ? "border-red-500" : "border-gray-300"
                  } ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                >
                  <option value="">Select Scenario Type</option>
                  {SCENARIO_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {errors.scenarioType && (
                <p className="text-red-500 text-sm">{errors.scenarioType}</p>
              )}
            </div>

            {/* Scenario Type Other - Show only when "Other" is selected */}
            {formData.scenarioType === "Other" && (
              <div className="flex flex-col gap-2 md:col-span-2">
                <label
                  htmlFor="scenarioTypeOther"
                  className="text-sm font-medium text-gray-700"
                >
                  Specify Scenario Type <span className="text-red-500">*</span>
                </label>
                <input
                  id="scenarioTypeOther"
                  type="text"
                  value={formData.scenarioTypeOther}
                  onChange={(e) =>
                    handleInputChange("scenarioTypeOther", e.target.value)
                  }
                  disabled={isViewMode}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                    errors.scenarioTypeOther
                      ? "border-red-500"
                      : "border-gray-300"
                  } ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  placeholder="Enter the scenario type name"
                  required
                />
                {errors.scenarioTypeOther && (
                  <p className="text-red-500 text-sm">
                    {errors.scenarioTypeOther}
                  </p>
                )}
              </div>
            )}

            {/* Assigned Role/Team */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="assignedRoleId"
                className="text-sm font-medium text-gray-700"
              >
                Assigned Role/Team <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="assignedRoleId"
                  value={formData.assignedRoleId}
                  onChange={(e) =>
                    handleInputChange("assignedRoleId", e.target.value)
                  }
                  disabled={rolesLoading || isViewMode}
                  className={`w-full p-2 border rounded-md appearance-none focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                    errors.assignedRoleId
                      ? "border-red-500"
                      : "border-gray-300"
                  } ${rolesLoading || isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  required
                >
                  <option value="">
                    {rolesLoading ? "Loading roles..." : "Select Role/Team"}
                  </option>
                  {rolesData.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {errors.assignedRoleId && (
                <p className="text-red-500 text-sm">{errors.assignedRoleId}</p>
              )}
              {rolesData.length === 0 && !rolesLoading && (
                <p className="text-amber-600 text-sm">
                  No roles found. Please create roles in Role Management first.
                </p>
              )}
            </div>

            {/* Response Time SLA */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="responseTimeSLA"
                className="text-sm font-medium text-gray-700"
              >
                Response Time SLA (Minutes) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="responseTimeSLA"
                min="1"
                step="1"
                value={formData.responseTimeSLA}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? "" : Number(e.target.value);
                  handleInputChange("responseTimeSLA", value);
                }}
                disabled={isViewMode}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.responseTimeSLA ? "border-red-500" : "border-gray-300"
                } ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                placeholder="Enter response time in minutes"
                required
              />
              {errors.responseTimeSLA && (
                <p className="text-red-500 text-sm">{errors.responseTimeSLA}</p>
              )}
              <p className="text-xs text-gray-500">
                Must be a positive number greater than 0
              </p>
            </div>
          </div>

          {/* Steps (Checklist) */}
          <div className="mt-6 flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">
                Steps (Checklist) <span className="text-red-500">*</span>
                <span className="text-gray-500 font-normal ml-2">
                  (Minimum 1 required)
                </span>
              </label>
              {!isViewMode && (
                <button
                  type="button"
                  onClick={handleAddStep}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-lg hover:bg-teal-600 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              )}
            </div>

            {formData.steps.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-500 mb-4">No steps added yet</p>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={handleAddStep}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Step
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {formData.steps.map((step) => (
                  <div
                    key={step.id}
                    className="flex items-start gap-3 p-4 border border-gray-300 rounded-lg bg-gray-50"
                  >
                    <div className="flex-shrink-0 mt-2">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0fa9a6] text-white rounded-full font-semibold text-sm">
                        {step.order}
                      </span>
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={step.stepDescription}
                        onChange={(e) =>
                          handleStepChange(step.id, e.target.value)
                        }
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998] resize-none ${
                          errors.steps && !step.stepDescription.trim()
                            ? "border-red-500"
                            : "border-gray-300"
                        } ${isViewMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder={`Enter step ${step.order} description...`}
                        rows={2}
                      />
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => handleRemoveStep(step.id)}
                        className="flex-shrink-0 mt-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={formData.steps.length === 1}
                        title={
                          formData.steps.length === 1
                            ? "At least one step is required"
                            : "Remove step"
                        }
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {errors.steps && (
              <p className="text-red-500 text-sm">{errors.steps}</p>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/external-emergency-preparedness/emergency-response-plans"
                  )
                }
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || rolesLoading}
                className="px-6 py-2 bg-[#0fa9a6] text-white rounded-lg hover:bg-teal-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                  ? "Update"
                  : "Submit"}
              </button>
            </div>
          )}
          {isViewMode && (
            <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/external-emergency-preparedness/emergency-response-plans"
                  )
                }
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddResponsePlan;

